import { useQuery } from '@tanstack/react-query';
import type { MonitorIncidentDetailDto, MonitorObservationDto } from '@cpm/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { Badge, Button, ErrorNote, Spinner } from './ui.js';

function timestamp(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function ObservationTable({ observations, empty }: { observations: MonitorObservationDto[]; empty: string }) {
  if (observations.length === 0) return <p className="text-slate-500 dark:text-slate-400">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead><tr><th className="pr-3">Observed</th><th className="pr-3">Result</th><th className="pr-3">HTTP</th><th className="pr-3">Latency</th><th>Detail</th></tr></thead>
        <tbody>
          {observations.map((observation) => (
            <tr key={observation.id} className="border-t border-slate-100 align-top dark:border-slate-800">
              <td className="py-2 pr-3 whitespace-nowrap">{timestamp(observation.observedAt)}</td>
              <td className="py-2 pr-3"><Badge tone={observation.status === 'failure' ? 'red' : observation.status === 'success' ? 'green' : 'blue'}>{observation.status}</Badge></td>
              <td className="py-2 pr-3">{observation.responseStatus ?? '—'}</td>
              <td className="py-2 pr-3 whitespace-nowrap">{observation.latencyMs == null ? '—' : `${observation.latencyMs} ms`}</td>
              <td className="min-w-48 max-w-sm break-words py-2">{observation.detail ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function NotificationIncident({ incidentId }: { incidentId: string }) {
  const [open, setOpen] = useState(false);
  const detail = useQuery({
    queryKey: ['monitoring', 'incident', incidentId],
    queryFn: () => api.get<MonitorIncidentDetailDto>(`/api/monitoring/incidents/${incidentId}`),
    enabled: open,
    refetchInterval: open ? 60_000 : false,
  });
  const data = detail.data;
  return (
    <div className="mt-2">
      <Button variant="secondary" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? 'Hide incident details' : 'View incident details'}
      </Button>
      {open && (
        <div className="mt-3 space-y-4 rounded-lg border border-slate-200 p-4 text-sm dark:border-slate-700">
          {detail.isPending ? <Spinner label="Loading incident details…" /> : detail.error ? (
            <>
              <ErrorNote error={detail.error} />
              <p>Incident details may be unavailable if the check was removed or monitoring is disabled.</p>
            </>
          ) : data && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">Recorded incident</span>
                <Badge tone={data.incident.status === 'open' ? 'red' : 'green'}>{data.incident.status}</Badge>
              </div>
              <p className="whitespace-pre-wrap break-words"><span className="font-medium">Recorded failure:</span> {data.incident.summary}</p>
              <dl className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
                {[
                  ['Opened', timestamp(data.incident.openedAt)],
                  ['Escalated', timestamp(data.incident.escalatedAt)],
                  ['Last observed', timestamp(data.incident.lastObservedAt)],
                  ['Recovered', timestamp(data.incident.resolvedAt)],
                ].map(([label, value]) => (
                  <div key={label}><dt className="text-slate-500">{label}</dt><dd>{value}</dd></div>
                ))}
              </dl>
              <section className="space-y-2 border-t border-slate-200 pt-3 dark:border-slate-700">
                <h3 className="font-medium">Incident window</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">Results around the opening and recovery of this incident. Later checks do not replace these results.</p>
                <ObservationTable observations={data.incidentObservations} empty="No incident samples remain in the 30-day observation history." />
              </section>
              <div className="border-t border-slate-200 pt-3 dark:border-slate-700">
                <p className="font-medium">Current check (live): {data.check.name}</p>
                <p>{data.check.subscriptionName} · <Badge tone={data.check.status === 'up' ? 'green' : data.check.status === 'down' || data.check.status === 'degraded' ? 'red' : 'yellow'}>{data.check.status}</Badge>{!data.check.enabled && ' · disabled'}</p>
                <p className="mt-1 break-all font-mono text-xs">{data.check.target}{data.check.port ? ` (port ${data.check.port})` : ''}</p>
                <p className="mt-1">
                  {data.check.kind === 'http' ? `Expected HTTP ${data.check.expectedStatuses.join(', ')}`
                    : data.check.kind === 'certificate' ? `Trusted certificate; warn within ${data.check.certificateWarnDays} days of expiry`
                      : 'Expected successful TCP connection'}
                </p>
                {data.check.expectedBodyContains && <p className="break-words">Required response text: {data.check.expectedBodyContains}</p>}
                <p className="mt-1 text-xs text-slate-500">Every {data.check.intervalSeconds} seconds · last checked {timestamp(data.check.lastCheckedAt)}</p>
                {data.check.lastError && <p className="mt-1 break-words text-red-600 dark:text-red-400">{data.check.lastError}</p>}
              </div>
              <details className="border-t border-slate-200 pt-3 dark:border-slate-700">
                <summary className="cursor-pointer font-medium text-brand-600 dark:text-brand-400">Show latest check results</summary>
                <div className="mt-3"><ObservationTable observations={data.observations} empty="No recent observations remain in history." /></div>
              </details>
              <Link className="inline-block text-brand-600 underline dark:text-brand-400" to={`/monitoring?subscription=${data.check.subscriptionId}&check=${data.check.id}`}>Open this check in Monitoring</Link>
              <p className="break-all text-xs text-slate-500">Incident: {data.incident.id}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
